import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../api/admin';
import { useFranchiseesQuery } from '../api/queries';
import AfAccessEditor from './AfAccessEditor';
import { showToast } from './Toast';
import type { UserRow } from '../types';

type Props = {
  user: UserRow | null;
  onClose: () => void;
  onMutated: () => void;
};

const AVATAR_COLOR: Record<UserRow['role'], string> = {
  admin: '#4338ca',
  fbc: '#92400e',
  franchisee: '#0284c7',
};

function initials(name: string | null | undefined, email: string): string {
  const src = name && name.trim().length > 0 ? name.trim() : email;
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

function statusPill(u: UserRow): { className: string; label: string } {
  if (u.pulseStatus === 'active') return { className: 'green', label: 'Active' };
  if (u.pulseStatus === 'pending') return { className: 'yellow', label: 'Pending Invite' };
  return { className: 'neutral', label: 'Unregistered' };
}

function formatDate(iso?: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

export default function UserDetailDrawer({ user, onClose, onMutated }: Props) {
  const open = user !== null;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRow['role']>('franchisee');
  const [busy, setBusy] = useState(false);
  const [afDraft, setAfDraft] = useState<string[]>([]);
  const [afBusy, setAfBusy] = useState(false);
  const { data: franchisees } = useFranchiseesQuery();

  useEffect(() => {
    if (!user) return;
    setName(user.name ?? '');
    setEmail(user.email);
    setRole(user.role);
    setAfDraft(user.afNumbers ?? []);
  }, [user]);

  const afDirty = useMemo(() => {
    const a = [...(user?.afNumbers ?? [])].sort();
    const b = [...afDraft].sort();
    return a.length !== b.length || a.some((x, i) => x !== b[i]);
  }, [afDraft, user]);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!user) {
    return (
      <>
        <div className={`call-detail-overlay`} />
        <div className={`call-detail-panel`} />
      </>
    );
  }

  async function saveChanges() {
    if (!user) return;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast('Invalid email address', { variant: 'error' });
      return;
    }
    setBusy(true);
    try {
      if (user.id === null) {
        await adminApi.createUser({
          email: email.trim().toLowerCase(),
          name: name.trim() || undefined,
          role,
          afNumbers: afDraft,
        });
        showToast(`Account created for ${name || email}`);
      } else {
        await adminApi.updateUser(user.id, {
          email: email.trim().toLowerCase(),
          name: name.trim(),
          role,
        });
        showToast('Changes saved');
      }
      onMutated();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite() {
    if (!user) return;
    if (!confirm(`Send invite to ${user.email}?`)) return;
    setBusy(true);
    try {
      await adminApi.sendInvite({
        email: user.email,
        name: user.name ?? undefined,
        role: user.role,
        afNumber: user.afNumber ?? undefined,
      });
      showToast(`Invite sent to ${user.email}`);
      onMutated();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function sendPasswordReset() {
    if (!user || user.id === null) return;
    if (!confirm(`Send a password-reset email to ${user.email}?`)) return;
    setBusy(true);
    try {
      await adminApi.sendPasswordReset(user.id);
      showToast(`Password-reset email sent to ${user.email}`);
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function saveAfAccess() {
    if (!user || user.id === null) return;
    setAfBusy(true);
    try {
      await adminApi.setAfAccess(user.id, afDraft);
      showToast('Franchise access updated');
      onMutated();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setAfBusy(false);
    }
  }

  async function deactivate() {
    if (!user || user.id === null) return;
    if (!confirm(`Deactivate ${user.name ?? user.email}? This clears their password.`)) return;
    setBusy(true);
    try {
      await adminApi.updateUser(user.id, { active: false });
      showToast(`${user.name ?? user.email} deactivated`);
      onMutated();
      onClose();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function deleteAccount() {
    if (!user || user.id === null) return;
    if (!confirm(`Permanently delete ${user.name ?? user.email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteUser(user.id);
      showToast(`${user.name ?? user.email} deleted`);
      onMutated();
      onClose();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const ini = initials(user.name, user.email);
  const avatarColor = AVATAR_COLOR[user.role];
  const sp = statusPill(user);

  return (
    <>
      <div className="call-detail-overlay open" onClick={onClose} />
      <div className="call-detail-panel open">
        <div className="cdp-header">
          <h3>User Details</h3>
          <button className="cdp-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cdp-body">
          <div className="user-detail-identity">
            <div
              className="user-detail-avatar"
              style={{
                background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}cc)`,
                boxShadow: `0 4px 12px ${avatarColor}25`,
              }}
            >
              {ini}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-detail-name">{user.name || 'Unnamed user'}</div>
              <div className="user-detail-email">{user.email}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <span className={`ds-role ${user.role}`}>{user.role}</span>
                <span className={`pill-status ${sp.className}`}>
                  <span className="dot" /> {sp.label}
                </span>
              </div>
            </div>
          </div>

          <div className="cdp-section">
            <div className="cdp-section-title">Edit Details</div>
            <div className="ds-field" style={{ marginBottom: 12 }}>
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="ds-field" style={{ marginBottom: 12 }}>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {user.id === null && (
                <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  Saving creates a Pulse account (no invite sent).
                </div>
              )}
            </div>
            <div className="ds-field" style={{ marginBottom: 12 }}>
              <label>Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as UserRow['role'])}
              >
                <option value="admin">Admin</option>
                <option value="fbc">FBC</option>
                <option value="franchisee">Franchisee</option>
              </select>
            </div>
            <button
              type="button"
              className="ds-btn ds-btn-primary"
              onClick={saveChanges}
              disabled={busy}
              style={{ width: '100%' }}
            >
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          <div className="cdp-section">
            <div className="cdp-section-title">Account</div>
            <div className="user-detail-grid">
              {user.afNumber && (
                <>
                  <div className="user-detail-label">AF Number</div>
                  <div className="user-detail-value">{user.afNumber}</div>
                </>
              )}
              {user.location && (
                <>
                  <div className="user-detail-label">Location</div>
                  <div className="user-detail-value">{user.location}</div>
                </>
              )}
              {user.fbcName && (
                <>
                  <div className="user-detail-label">FBC</div>
                  <div className="user-detail-value">{user.fbcName}</div>
                </>
              )}
              <div className="user-detail-label">Created</div>
              <div className="user-detail-value">
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Unknown'}
              </div>
              <div className="user-detail-label">Last Login</div>
              <div className="user-detail-value">{formatDate(user.lastLogin)}</div>
            </div>
          </div>

          <div className="cdp-section">
            <div className="cdp-section-title">
              Franchise Access ({afDraft.length})
            </div>
            <AfAccessEditor
              value={afDraft}
              onChange={setAfDraft}
              franchisees={franchisees}
              disabled={user.id === null}
            />
            {user.id === null ? (
              <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 8 }}>
                Saved with the account when you click <em>Save Changes</em> above.
              </div>
            ) : (
              <button
                type="button"
                className="ds-btn ds-btn-primary"
                onClick={saveAfAccess}
                disabled={afBusy || !afDirty}
                style={{ width: '100%', marginTop: 12 }}
              >
                {afBusy ? 'Saving…' : afDirty ? 'Save Franchise Access' : 'No changes'}
              </button>
            )}
          </div>

          <div className="cdp-section">
            <div className="cdp-section-title">Actions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(user.pulseStatus === 'not_registered' || user.pulseStatus === 'pending') && (
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={sendInvite}
                  disabled={busy}
                >
                  Send Setup Invite
                </button>
              )}
              {user.pulseStatus === 'active' && user.id !== null && (
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={sendPasswordReset}
                  disabled={busy}
                >
                  Send Password Reset
                </button>
              )}
              {user.pulseStatus === 'active' && user.id !== null && (
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={deactivate}
                  disabled={busy}
                >
                  Deactivate Account
                </button>
              )}
              {user.id !== null && (
                <button
                  type="button"
                  className="ds-btn-danger"
                  onClick={deleteAccount}
                  disabled={busy}
                >
                  Delete User Permanently
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
